import React, { useCallback, useMemo, useState } from 'react';
import CloseButton from '../CloseButton/CloseButton';
import styles from './AnalyticsInfo.module.scss';
import { useTranslation } from 'react-i18next';
import Icon from '../Icon/Icon';
import cx from 'classnames';
import { parseString } from '../../utils/stringHelper';
import MiniUserDetail from '../MiniUserDetail/MiniUserDetail';
import AnalyticsViewer from './AnalyticsViewer';
import useAccessibleClick from '../../../common/hooks/useAccessibleClick';
import AnalyticsCallToAction from './AnalyticsCallToAction';
import { formatToDateString } from '../../utils/TimeUtil';

const pageTypes = {
  mainInfo: 'mainInfo',
  userInfo: 'userInfo',
  callToActionInfo: 'callToActionInfo',
};

const AnalyticsInfo = ({ info = [], onClose, isMobileView, inputRef, className }) => {
  const [pageType, setPageType] = useState(pageTypes.mainInfo);
  const [selectedUser, setSelectedUser] = useState({});
  const [selectedCallToAction, setSelectedCallToAction] = useState({});

  const allViews = useMemo(() => info?.views?.reduce((acc, { views }) => acc + views, 0), [info]);
  const currentCallToActionClicks = users => users?.reduce((acc, { clicks }) => acc + clicks, 0) || 0;
  const latestViewDate = useMemo(() => {
    if (!info?.views?.length) return null;
    const dates = info.views
      .map(({ latestViewDate }) => latestViewDate)
      .filter(Boolean)
      .map(date => new Date(date))
      .filter(date => !isNaN(date.getTime()));
    if (dates.length === 0) return null;
    return new Date(Math.max(...dates)).toISOString();
  }, [info]);

  const { t } = useTranslation();

  const handleCloseDetailsInfo = () => {
    setSelectedUser({});
    setSelectedCallToAction({});
    setPageType(pageTypes.mainInfo);
  };

  const backUserRef = useAccessibleClick(handleCloseDetailsInfo)
  const backCtaRef = useAccessibleClick(handleCloseDetailsInfo)

  const handleOpenUserInfo = user => {
    setSelectedUser(user);
    setPageType(pageTypes.userInfo);
  };

  const handleOpenCallToActionInfo = cta => {
    setSelectedCallToAction(cta);
    setPageType(pageTypes.callToActionInfo);
  };

  return (
    <div ref={inputRef} className={cx(styles.mainContainer, className, { [styles.mobileContainer]: isMobileView })}>
      <div className={cx(styles.mainWrapper, { [styles.hide]: pageType !== pageTypes.mainInfo })}>
        <div className={styles.closeContainer}>
          <CloseButton onClose={onClose} />
        </div>
        {/* Users general */}
        <span tabIndex={0} className={styles.titleSection}>{t('analytics.engagement_insights')}</span>
        {latestViewDate && (
          <div tabIndex={0} className={styles.latestViewContainer}>
            <span className={styles.latestViewText}>{t('views.latest_view')}</span>
            <span 
              className={styles.latestViewDate}
              title={formatToDateString(latestViewDate, 'en-US', true)}
            >
              {formatToDateString(latestViewDate, 'en-US', false)}
            </span>
          </div>
        )}
        <div className={styles.totalViewsContainer}>
          <span tabIndex={0} className={styles.totalViewsText}>{t('views.total_views')}</span>
          <span tabIndex={0} className={styles.totalViewsAmount}>{allViews}</span>
        </div>
        <div className={styles.uniqueViewersContainer}>
          <span tabIndex={0} className={styles.uniqueViewers}>
            {info?.views?.length} {t('views.unique_viewers')}
          </span>
          <span tabIndex={0} className={styles.clickTooltip}>{t('views.click_tooltip')}</span>
        </div>
        <div className={styles.viewerList}>
          {info?.views?.map(({ user, userId, views, latestViewDate }) =>
            <AnalyticsViewer
              key={userId}
              user={user}
              userId={userId}
              views={views}
              latestViewDate={latestViewDate}
              handleOpenUserInfo={handleOpenUserInfo}
            />
          )}
        </div>
        {/* Call-to-actions general */}
        <div className={styles.ctaWrapper}>
          <span tabIndex={0} className={styles.titleSection}>{t('analytics.cta_insights')}</span>
          <div tabIndex={0} className={styles.ctaText}>
            <span>{t('analytics.cta')}</span>
            <span>{t('analytics.individual_clicks')}</span>
          </div>
          <div className={styles.actionsWrapper}>
            {info?.cta?.map(action =>
              <AnalyticsCallToAction
                key={action.id}
                action={action}
                handleOpenCallToActionInfo={() => handleOpenCallToActionInfo(action)}
                currentCta={currentCallToActionClicks(action.users)}
              />
            )}
          </div>
        </div>
      </div>
      {/*User details*/}
      <div className={cx(styles.userDetails, { [styles.hide]: !(pageType === pageTypes.userInfo) })}>
        <div className={styles.closeContainerDetails}>
          <div ref={backUserRef} tabIndex={0} className={styles.backContainer} onClick={handleCloseDetailsInfo}>
            <Icon src="viewsBackIcon" className={styles.backIcon} />
            <span>{t('views.back')}</span>
          </div>
          <CloseButton tabIndex onClose={onClose} />
        </div>
        <div className={styles.viewerItem}>
          <div className={styles.imageContainer}>
            {selectedUser.user ? (
              <div tabIndex={0}>
                {selectedUser.user?.avatar ? <img className={styles.image} src={selectedUser.user?.avatar} alt={t('clip_organizer.add_modal.avatar')} aria-label={t('clip_organizer.add_modal.avatar')} />
                  : <span tabIndex={0}>{parseString(selectedUser.user?.name)[0]?.toUpperCase()}</span>}
              </div>
            ) : (
              <Icon className={styles.placeholder} src="viewerPlaceholder" />
            )}
          </div>
          <span tabIndex={0} className={styles.viewerName}>{selectedUser.user?.name ? parseString(selectedUser.user?.name) : t('views.anonymous')}</span>
        </div>
        {selectedUser.latestViewDate && (
          <div tabIndex={0} className={styles.latestViewContainer}>
            <span className={styles.latestViewText}>{t('views.latest_view')}</span>
            <span 
              className={styles.latestViewDate}
              title={formatToDateString(selectedUser.latestViewDate, 'en-US', true)}
            >
              {formatToDateString(selectedUser.latestViewDate, 'en-US', false)}
            </span>
          </div>
        )}
        <div tabIndex={0} className={styles.totalViewsContainer}>
          <span className={styles.totalViewsText}>{t('views.total_views')}</span>
          <span className={styles.totalViewsAmount}>{selectedUser.views || 0}</span>
        </div>
      </div>
      {/*Call-to-action details*/}
      <div className={cx(styles.containerDetails, { [styles.hide]: !(pageType === pageTypes.callToActionInfo) })}>
        <div className={styles.closeContainerDetails}>
          <div tabIndex={0} ref={backCtaRef} className={styles.backContainer} onClick={handleCloseDetailsInfo}>
            <Icon src="viewsBackIcon" className={styles.backIcon} />
            <span>{t('views.back')}</span>
          </div>
          <CloseButton onClose={onClose} />
        </div>
        <div className={styles.actionDetails}>
          {selectedCallToAction?.users?.map(({ user, clicks }) => (
            <div key={user?.username} className={styles.ctaUser}>
              <div tabIndex={0} className={styles.ctaUserItem}>
                {user?.name ? (
                  <MiniUserDetail
                    avatar={user.avatar}
                    size="medium"
                    name={parseString(user.name)}
                    classNames={{
                      containerClassName: styles.profile,
                      avatarClassName: styles.ctaUserAvatar,
                      titleClassName: styles.ctaUserName,
                    }}
                  />
                ) : (
                  <div tabIndex={0}>
                    <div className={styles.imageContainer}>
                      <span tabIndex={0}>{clicks}</span>
                    </div>
                    <span tabIndex={0} className={styles.viewerName}>{t('views.anonymous')}</span>
                  </div>
                )}
              </div>
              <span tabIndex={0} className={styles.ctaItemCount} aria-label={`${clicks} ${t('views.total_views')}`}>{clicks}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsInfo;
